import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ChartCard from "../../../components/ui/ChartCard";
import {
  usePdfExport,
  usePdfExportSection,
} from "../../../context/PdfExportContext";
import { buildThickMultiChoiceOptions } from "./chartOptions";
import StructuredPeriodAxis from "./StructuredPeriodAxis";
import {
  buildMultiChoicePeriodChart,
  groupTextResponses,
} from "./resultHelpers";

function estimateWrappedLineCount(label, maximumLineLength = 22) {
  const words = String(label || "").split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return 1;
  }

  let lines = 1;
  let lineLength = 0;
  words.forEach((word) => {
    const wordLength = word.length;
    if (lineLength === 0) {
      lines += Math.max(0, Math.ceil(wordLength / maximumLineLength) - 1);
      lineLength = wordLength % maximumLineLength || maximumLineLength;
    } else if (lineLength + 1 + wordLength <= maximumLineLength) {
      lineLength += 1 + wordLength;
    } else {
      lines += Math.max(1, Math.ceil(wordLength / maximumLineLength));
      lineLength = wordLength % maximumLineLength || maximumLineLength;
    }
  });
  return lines;
}

export function TextResponsePanel({
  question,
  sections,
  availableIssues,
  height,
  title,
  standalone = false,
}) {
  const pdfExport = usePdfExport();
  const pdfSection = usePdfExportSection();
  const panelSections = useMemo(
    () => (sections?.length ? sections : [{ question, title: null }]),
    [question, sections]
  );
  const preparedSections = panelSections.map((section) => ({
    ...section,
    groups: groupTextResponses(section.question?.items || [], availableIssues),
  }));
  const answerCount = preparedSections.reduce(
    (total, section) =>
      total +
      (section.question?.items || []).reduce(
        (sectionTotal, item) => sectionTotal + (Number(item.count) || 0),
        0
      ),
    0
  );

  useEffect(() => {
    if (!pdfSection?.sectionKey || !pdfExport?.registerExportable) {
      return undefined;
    }
    const issueLabels = new Map(
      (availableIssues || []).map((issue) => [issue.value, issue.label])
    );
    return pdfExport.registerExportable({
      sectionKey: pdfSection.sectionKey,
      order: (pdfSection.order || 0) + 50,
      getContent: async () => ({
        kind: "table",
        title: title || question?.text || "Tekstantwoorden",
        rows: panelSections.flatMap((section) =>
          (section.question?.items || []).map((item) => ({
            period: issueLabels.get(item.issue) || item.issue,
            text: item.text,
            count: Number(item.count) || 0,
          }))
        ),
      }),
    });
  }, [
    availableIssues,
    panelSections,
    pdfExport,
    pdfSection,
    question?.text,
    title,
  ]);

  return (
    <aside
      className={`loonopzand-dashboard__text-panel${
        standalone ? " loonopzand-dashboard__text-panel--standalone" : ""
      }`}
      style={height ? { height: `${height}px` } : undefined}
    >
      <header className="loonopzand-dashboard__text-panel-header">
        <h3>{title || question?.text || "Anders, namelijk:"}</h3>
        <span>{answerCount} antwoorden</span>
      </header>
      <div className="loonopzand-dashboard__text-panel-body">
        {answerCount === 0 ? (
          <div className="loonopzand-dashboard__text-empty">
            Geen ingevulde antwoorden voor de geselecteerde periodes.
          </div>
        ) : (
          preparedSections.map((section, sectionIndex) => (
            section.groups.length > 0 && (
              <section
                key={section.question?.question_id || sectionIndex}
                className="loonopzand-dashboard__text-section"
              >
                {section.title && <h4>{section.title}</h4>}
                {section.groups.map((yearGroup) => (
                  <section key={yearGroup.year} className="loonopzand-dashboard__text-year">
                    <h4>{yearGroup.year}</h4>
                    {yearGroup.periods.map((period) => (
                      <div key={period.issue} className="loonopzand-dashboard__text-period">
                        <h5>Q{period.quarter}</h5>
                        <div className="loonopzand-dashboard__text-rows">
                          {period.items.map((item) => (
                            <div
                              key={`${period.issue}-${item.text}`}
                              className="loonopzand-dashboard__text-row"
                            >
                              <span>{item.text}</span>
                              <strong>{item.count}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </section>
                ))}
              </section>
            )
          ))
        )}
      </div>
    </aside>
  );
}

export default function MultiChoiceWithTextPanel({
  question,
  textQuestion,
  filters,
  mode,
  info,
  textPanelTitle,
  textSections,
  excludeUnansweredIssues = false,
}) {
  const chartContainerRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(null);
  const showTextPanel = Boolean(textQuestion || textSections?.length);
  const chartFilters = excludeUnansweredIssues
    ? {
        ...filters,
        selected_issues: (filters?.selected_issues || []).filter((issue) =>
          question.options.some((option) => {
            const value = option.by_issue?.find((item) => item.issue === issue);
            return Number(value?.count) > 0;
          })
        ),
      }
    : filters;
  const chart = buildMultiChoicePeriodChart(question, chartFilters, mode);
  const periodCount = Math.max(chartFilters?.selected_issues?.length || 0, 1);
  const periodBandHeight = Math.max(periodCount * 20, 42);
  const wrappedLabelLines = Math.max(
    1,
    ...chart.categories.map((category) => estimateWrappedLineCount(category))
  );
  // Apex gives every category the same row height. Keep that row at least as
  // tall as the longest fully wrapped side label, even when filtering leaves
  // only one period. This prevents adjacent category labels from overlapping.
  const labelBandHeight = wrappedLabelLines * 15 + 12;
  const categoryBandHeight = Math.max(periodBandHeight, labelBandHeight);
  const chartHeight = Math.max(
    250,
    chart.categories.length * categoryBandHeight + 100
  );
  const chartOptions = buildThickMultiChoiceOptions(
    chart.categories,
    chart.series,
    chart.details,
    mode
  );
  // Increasing the category row should add whitespace, not make the bars
  // thicker. Preserve their previous approximate pixel height.
  chartOptions.plotOptions.bar.barHeight = `${Math.min(
    88,
    (88 * periodBandHeight) / categoryBandHeight
  )}%`;
  chartOptions.yaxis = {
    ...(chartOptions.yaxis || {}),
    labels: { show: false },
  };
  chartOptions.grid = {
    ...(chartOptions.grid || {}),
    padding: {
      ...(chartOptions.grid?.padding || {}),
      left: 0,
    },
  };
  chartOptions.fullscreenOverrides = {
    ...(chartOptions.fullscreenOverrides || {}),
    yaxis: { labels: { show: false } },
  };

  useLayoutEffect(() => {
    const chartContainer = chartContainerRef.current;
    if (!chartContainer) {
      return undefined;
    }

    const syncHeight = () => {
      setPanelHeight(Math.round(chartContainer.getBoundingClientRect().height));
    };

    syncHeight();
    const resizeObserver = new ResizeObserver(syncHeight);
    resizeObserver.observe(chartContainer);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      className={`loonopzand-dashboard__choice-with-text${
        showTextPanel ? "" : " loonopzand-dashboard__choice-with-text--chart-only"
      }`}
    >
      <div ref={chartContainerRef} className="loonopzand-dashboard__choice-chart">
        <ChartCard
          containerClassName="loonopzand-choice-chart-card"
          chartKey={`loonopzand-${question.question_id}-${mode}-${chartFilters?.selected_issues?.join("-")}`}
          title={question.text}
          info={info || `${question.answered_count} van ${question.response_count} respondenten beantwoordden deze vraag.`}
          type="bar"
          options={chartOptions}
          series={chart.series}
          height={chartHeight}
          chartSideContent={(
            <StructuredPeriodAxis
              compact
              groups={chart.categories.map((category, index) => ({
                key: `${question.question_id}-${index}`,
                label: category,
                issues: (chartFilters?.selected_issues || []).filter(
                  (_issue, seriesIndex) =>
                    Number(chart.details?.[seriesIndex]?.[index]?.count) > 0
                ),
              }))}
              issues={chartFilters?.selected_issues || []}
            />
          )}
        />
      </div>
      {showTextPanel && (
        <TextResponsePanel
          question={textQuestion}
          sections={textSections}
          availableIssues={filters?.available_issues || []}
          height={panelHeight}
          title={textPanelTitle}
        />
      )}
    </div>
  );
}
